import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import i18n from '../i18n';
import { detectCountryAndCurrency, fetchExchangeRates } from './geoCurrencyService';
import {
  getCurrencyForCountry,
  getLanguageForCountry,
  getLocaleForCurrency,
  DEFAULT_CURRENCY,
  DEFAULT_LOCALE,
} from './countryMap';
import { formatPrice, formatPriceChunks, convertPriceString } from './priceFormat';

export const LANG_USER_CHOICE_KEY = 'bv_lang_user_selected';

const GeoPricingContext = createContext(null);

const initialState = {
  loading: true,
  countryCode: null,
  currencyCode: DEFAULT_CURRENCY,
  locale: DEFAULT_LOCALE,
  rate: 1,
};

export function GeoPricingProvider({ children }) {
  const [state, setState] = useState(initialState);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      const { countryCode, currencyCode: apiCurrencyCode } = await detectCountryAndCurrency();
      if (cancelled) return;

      // Prefer the currency the IP lookup itself reports; fall back to the
      // static country->currency table when the API didn't return one.
      const requestedCurrency = apiCurrencyCode || getCurrencyForCountry(countryCode);

      let currencyCode = requestedCurrency;
      let rate = 1;
      if (requestedCurrency !== DEFAULT_CURRENCY) {
        const rates = await fetchExchangeRates();
        if (cancelled) return;
        if (rates && typeof rates[requestedCurrency] === 'number') {
          rate = rates[requestedCurrency];
        } else {
          // No rate available for this currency — show USD instead.
          currencyCode = DEFAULT_CURRENCY;
        }
      }

      const locale = getLocaleForCurrency(currencyCode);

      if (cancelled) return;
      setState({ loading: false, countryCode, currencyCode, locale, rate });

      const userChoseLanguage = window.localStorage.getItem(LANG_USER_CHOICE_KEY);
      if (!userChoseLanguage) {
        const detectedLang = getLanguageForCountry(countryCode);
        if (detectedLang && detectedLang !== i18n.language) {
          i18n.changeLanguage(detectedLang);
        }
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo(() => {
    const ctx = { locale: state.locale, currencyCode: state.currencyCode, rate: state.rate };
    return {
      ...state,
      formatPrice: (usdValue) => formatPrice(usdValue, ctx),
      formatPriceChunks: (usdValue) => formatPriceChunks(usdValue, ctx),
      convertPriceString: (text) => convertPriceString(text, ctx),
    };
  }, [state]);

  return <GeoPricingContext.Provider value={value}>{children}</GeoPricingContext.Provider>;
}

export function useGeoPricing() {
  const ctx = useContext(GeoPricingContext);
  if (!ctx) {
    throw new Error('useGeoPricing must be used within a GeoPricingProvider');
  }
  return ctx;
}
